import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import { Button, Checkbox, Col, Form, message, Row, Select, Space } from 'antd'
import { Maybe } from 'graphql/jsutils/Maybe'
import { FormChangeInfo, FormFinishInfo } from 'rc-field-form/es/FormContext'
import React, { useCallback, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { queryCache } from '../../apollo/hooks'
import { DEMANDE_INSERT } from '../../apollo/mutation/demande.mutation'
import { COPROPRIETE_GN_GS } from '../../apollo/query/copropriete.query'
import { KANBAN_QUERY } from '../../apollo/query/kanban.query'
import { PERSONNE_DETAIL_BY_ID } from '../../apollo/query/personne.query'
import { SOCIETE_DETAIL_BY_ID } from '../../apollo/query/societe.query'
import { useImperativeQuery } from '../../helper'
import { useAuth } from '../../lib/hooks'
import {
  CreateDemandeInput,
  D4_Batiment,
  D4_Copropriete,
  D4_Demande,
  D4_Kanban,
  D4_Personne,
  D4_Societe,
  D4_Symptometypeequipement,
  D4_Typeequipement,
  Demande,
} from '../../types'
import MSGraphAPI from '../Courriel/ms-graph.api'
import PersonneDescription from '../Description/PersonneDescription'
import SocieteDescription from '../Description/SocieteDescription'
import SelectMultiSources from '../SelectMultiSources/SelectMultiSources'
import DemandeExpressEditor from './DemandeExpressEditor'

const generateBreadcrumb = (batiment: Maybe<string>, equipement: Maybe<string>, symptome: Maybe<string>): string => {
  return `${batiment ?? 'Général'}${equipement ? ` > ${equipement}` : ''}${symptome ? ` > ${symptome}` : ''} `
}

const getEmetteurId = (context: any) => {
  if (context.type === 'personne') {
    return context.data.lot_coproprietaire?.coproprietaire.personne.id
  } else if (context.type === 'societe') {
    return context.data.lot_coproprietaire?.coproprietaire.societe?.representant_id
  }
  return null
}

type CreateDemandeInputs = CreateDemandeInput & {
  batimentLabel?: string
  batimentId: number
  equipementLabel?: string
  gestionnaire?: number
}

type DemandeExpressFormProps = {
  onValuesChange?: (values: FormValuesChanges) => void
  handleClose?: Function
  withList?: boolean
  withPersonneDescription?: boolean
  withSocieteDescription?: boolean
  withTextInput?: boolean
  withGestionnaires?: boolean
  onFetchDemandes?: (demandes: D4_Demande[] | undefined) => void
  onCreateDemandeFinish?: (demande: Demande) => void
  contextDefaultValue?: ContextDefaultValue
}

interface FormValues {
  context: string
  batiment: number
  equipement: number
  symptome: number
  kanban: number
  demande: string
}

const DemandeExpressForm: React.FC<DemandeExpressFormProps> = ({
  handleClose,
  onValuesChange,
  withList = false,
  withPersonneDescription = false,
  withSocieteDescription = false,
  withTextInput = false,
  withGestionnaires = false,
  onFetchDemandes,
  onCreateDemandeFinish,
  contextDefaultValue,
  children,
}) => {
  const { push } = useHistory()
  const [form] = Form.useForm<FormValues>()
  const [context, setContext] = useState({} as { coproprieteId: number; id: number; type: string; serialized: unknown })
  const { data: kanbans } = useQuery<{ kanban: D4_Kanban[] }>(KANBAN_QUERY)
  const [openDemande, setOpenDemande] = useState(false)
  const [fetchPersonne, { data: fetchPersonneData }] = useLazyQuery<{ personneDetail: D4_Personne }, { id: number }>(
    PERSONNE_DETAIL_BY_ID
  )
  const [fetchSociete, { data: fetchSocieteData }] = useLazyQuery<{ societe: D4_Societe }, { id: number }>(
    SOCIETE_DETAIL_BY_ID
  )
  const [createDemande] = useMutation<{ demande: Demande }, CreateDemandeInput>(DEMANDE_INSERT, {
    fetchPolicy: 'no-cache',
  })

  const handleFormsChanges = useCallback(
    async (name: string, infos: FormChangeInfo) => {
      //@ts-expect-error
      onValuesChange?.({ name, infos })
      // name = le nom du formulaire
      const { forms } = infos
      if (name === 'selectMultiSources') {
        const {
          contextId,
          type,
          batiment: bId,
          equipement: eId,
          symptome: sId,
        } = forms.selectMultiSources.getFieldsValue()

        const typeequipement = queryCache<D4_Typeequipement>({
          typename: 'd4_typeequipement',
          id: eId,
          fields: ['typeequipement'],
        })
        const batiment = queryCache<D4_Batiment>({
          typename: 'd4_batiment',
          id: bId,
          fields: ['libelle'],
        })
        const symptome = queryCache<D4_Symptometypeequipement>({
          typename: 'd4_symptometypeequipement',
          id: sId,
          fields: ['symptome'],
        })
        /**
         * Modifier le breadCrumb dans le textarea eg: Général > Accès Garage > Sécurisation
         */
        forms.demandeExpress.setFieldsValue({
          demande: generateBreadcrumb(batiment?.libelle, typeequipement?.typeequipement, symptome?.symptome),
        })

        if (withPersonneDescription && type === 'personne') {
          await fetchPersonne({ variables: { id: contextId ?? -1 } })
        } else if (withSocieteDescription && type === 'societe') {
          await fetchSociete({ variables: { id: contextId ?? -1 } })
        }
      }
    },
    [fetchPersonne, fetchSociete, onValuesChange, withPersonneDescription, withSocieteDescription]
  )

  const { user } = useAuth()
  const getCopro = useImperativeQuery<{ copropriete: D4_Copropriete }>(COPROPRIETE_GN_GS)

  const insertDemande = useCallback(
    async (values: CreateDemandeInputs, callback: (res: any) => void) => {
      const variables = {} as CreateDemandeInputs
      // Equipement
      variables.typeEquipementId = values.typeEquipementId
      variables.equipementCoproId = values.batimentId === values.coproprieteId ? values.coproprieteId : null
      variables.equipementBatimentId = variables.equipementCoproId ? null : Number(values.batimentId)
      // Demande
      variables.coproprieteId = values.coproprieteId
      variables.demande = values.demande.trim()
      variables.emetteurId = values.emetteurId // context?.representantId ?? context?.id pour les demande de rapels
      // Journal Etat Kanbans
      variables.kanban = values.kanban ?? null
      variables.gestionnairequidoitrappelerId = values.gestionnaire
      // values.coproprieteId = Batiment Général = doit etre mis sur null
      const bat = values.batimentId !== values.coproprieteId ? values.batimentLabel : null
      variables.equipementLibelle = `${values.equipementLabel ?? ''} ${bat ?? ''}`.trim() ?? null
      variables.dtype = values.dtype

      const copro = await getCopro({ id: values.coproprieteId })

      const res = await createDemande({ variables })

      if (
        copro.data.copropriete.gestionnaire?.id !== Number(user.personne.id) &&
        copro.data.copropriete.gestionnaireSedentaire?.id !== Number(user.personne.id)
      ) {
        await MSGraphAPI.sendEmail({
          data: {
            message: {
              subject: `[${copro.data.copropriete.trigramme}] Nouvelle Demande (${res.data?.demande.id})`,
              body: {
                contentType: 'html',
                content: `
              Bonjour,</br>
              Je viens de créer une nouvelle demande sur une copropriété dont vous êtes gestionnaire.</br></br>
              <strong>copro:</strong> ${copro.data.copropriete.trigramme}</br>
              <strong>objet de la demande:</strong> ${values.demande}</br></br>

              ${user.personne.prenom} ${user.personne.nom}
              `,
              },
              toRecipients: [
                { emailAddress: { address: copro.data.copropriete.gestionnaire?.mail1 } },
                { emailAddress: { address: copro.data.copropriete.gestionnaireSedentaire?.mail1 } },
              ],
            },
          },
        })
      }

      /**
       * envoyer un mail au gn/gs si le createur n'est pas un gestionnaire de la copropriete
       */

      callback(res)

      form.resetFields()
    },
    [createDemande, form, getCopro, user.personne.id, user.personne.nom, user.personne.prenom]
  )

  const handleCreateDemande = useCallback(
    async (values: any) => {
      const typeequipement = queryCache<D4_Typeequipement>({
        typename: 'd4_typeequipement',
        id: values.equipement,
        fields: ['typeequipement'],
      })

      const batiment = queryCache<D4_Batiment>({
        typename: 'd4_batiment',
        id: values.batiment,
        fields: ['libelle'],
      })

      const data: CreateDemandeInputs = {
        typeEquipementId: values.equipement,
        coproprieteId: values.coproprieteId,
        demande: values.demande,
        batimentId: values.batiment,
        emetteurId: getEmetteurId(context),
        kanban: values.kanban,
        equipementBatimentId: values.batiment,
        batimentLabel: batiment?.libellecourt ?? '',
        equipementLabel: typeequipement?.typeequipement ?? '',
        equipementLibelle: '',
        gestionnaire: values.gestionnaire,
        dtype: values.sinistre ? 'Sinistre' : values.gestionnaire ? 'DemandeDeRappel' : 'Demande',
      }

      insertDemande(data, (res) => {
        const demandeId = res.data?.demande.id

        if (demandeId) {
          form.resetFields()
          handleClose?.()
          onCreateDemandeFinish?.(res.data?.demande) ?? message.success('La demande à bien été crée')
          openDemande && push({ search: `?demandeId=${demandeId}` })
          setOpenDemande(false)
        }
      })
    },
    [context, insertDemande, form, handleClose, onCreateDemandeFinish, openDemande, push]
  )

  const handleFormsFinish = async (name: string, { values, forms }: FormFinishInfo) => {
    try {
      await forms.demandeExpress.validateFields()
      await forms.selectMultiSources.validateFields()
    } catch (error) {
      return
    }
    const v = { ...values, ...forms.selectMultiSources.getFieldsValue() }
    handleCreateDemande(v)
  }

  return (
    <Form.Provider onFormChange={handleFormsChanges} onFormFinish={handleFormsFinish}>
      <SelectMultiSources
        size="large"
        onSelectContext={setContext}
        withGestionnaires={withGestionnaires}
        contextDefaultValue={contextDefaultValue}
      />
      <Form size="large" form={form} name="demandeExpress" layout="vertical">
        <Row justify="center" gutter={[24, 12]}>
          {context?.type === 'personne' && withPersonneDescription && (
            <Col span={24}>
              <PersonneDescription personne={fetchPersonneData?.personneDetail} />
            </Col>
          )}
          {context?.type === 'societe' && fetchSocieteData?.societe && withSocieteDescription && (
            <Col span={24}>
              <SocieteDescription societe={fetchSocieteData?.societe} />
            </Col>
          )}
        </Row>
        <Row justify="center" gutter={[0, 0]}>
          {children}
          {withTextInput && (
            <Col xs={24} sm={12} md={24}>
              <Form.Item name="demande" rules={[{ required: true }]}>
                <DemandeExpressEditor />
              </Form.Item>
            </Col>
          )}
          <Col xs={24} sm={12} md={24}>
            <Form.Item name="kanban">
              <Select
                placeholder="Kanban"
                showSearch
                optionFilterProp="label"
                options={
                  kanbans?.kanban.map((kanban) => ({
                    value: kanban.etatkanbans[0].id,
                    label: kanban.titre,
                  })) ?? []
                }
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={24}>
            <Form.Item name="sinistre" valuePropName="checked">
              <Checkbox>Sinistre</Checkbox>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={24}>
            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit">
                  Enregistrer
                </Button>
                <Button htmlType="submit" onClick={() => setOpenDemande(true)}>
                  Enregistrer et ouvrir
                </Button>
              </Space>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Form.Provider>
  )
}

export default DemandeExpressForm
